import { useState } from "react";
import type { CSSProperties } from "react";
import { Mail, MapPin, MessageCircle, Phone, Send } from "lucide-react";
import { CONTACT } from "../data/site";

const INTERESTS = [
  "Fiber Optic Communication Drone",
  "FPV Kamikaze Drone",
  "Soccer Drone",
  "Trainer Drone",
  "RF Training Simulator",
  "UAS Detection System",
  "Durgastra GCS",
  "Military & Defence Training",
  "Custom Drone Build",
  "Maintenance & Repair",
  "Product Engineering Service",
  "Other",
];

const emptyForm = {
  name: "",
  organisation: "",
  interest: INTERESTS[0],
  message: "",
};

export const ContactSection = () => {
  const [form, setForm] = useState(emptyForm);

  const update = (key: keyof typeof form) => (value: string) =>
    setForm((previous) => ({ ...previous, [key]: value }));

  const subject = `Enquiry — ${form.interest}${form.organisation ? ` — ${form.organisation}` : ""}`;

  const body = [
    `Name: ${form.name || "-"}`,
    `Organisation: ${form.organisation || "-"}`,
    `Interested in: ${form.interest}`,
    "",
    form.message || "-",
  ].join("\n");

  const mailHref = `mailto:${CONTACT.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  const whatsAppHref = `https://wa.me/${CONTACT.whatsApp}?text=${encodeURIComponent(`${subject}\n\n${body}`)}`;

  return (
    <section className="contact-section" id="contact">
      <div className="contact-inner">
        <div className="contact-intro" data-reveal>
          <span className="eyebrow">Get In Touch</span>
          <h2>Talk To Our Team</h2>
          <div className="capability-rule" />
          <p>
            Tell us what you need and reach us the way you prefer — call, WhatsApp or email. We reply
            to every enquiry.
          </p>

          <div className="contact-channels">
            <article className="channel-card">
              <span className="icon-ring">
                <Phone size={22} strokeWidth={1.6} />
              </span>
              <h3>Call &amp; WhatsApp</h3>
              {CONTACT.phones.map(({ label, raw }) => (
                <a key={raw} href={`tel:+${raw}`}>
                  {label}
                </a>
              ))}
              <a
                className="channel-link"
                href={`https://wa.me/${CONTACT.whatsApp}`}
                target="_blank"
                rel="noreferrer"
              >
                <MessageCircle size={14} strokeWidth={1.9} />
                Chat on WhatsApp
              </a>
            </article>

            <article className="channel-card">
              <span className="icon-ring">
                <Mail size={22} strokeWidth={1.6} />
              </span>
              <h3>Email</h3>
              <a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a>
            </article>

            <article className="channel-card">
              <span className="icon-ring">
                <MapPin size={22} strokeWidth={1.6} />
              </span>
              <h3>Visit Us</h3>
              <p>{CONTACT.address}</p>
            </article>
          </div>
        </div>

        <form
          className="contact-form"
          data-reveal
          style={{ "--d": "120ms" } as CSSProperties}
          onSubmit={(event) => event.preventDefault()}
        >
          <h3>Send An Enquiry</h3>

          <label>
            <span>Your name</span>
            <input
              type="text"
              value={form.name}
              placeholder="Full name"
              onChange={(event) => update("name")(event.target.value)}
            />
          </label>

          <label>
            <span>Organisation</span>
            <input
              type="text"
              value={form.organisation}
              placeholder="Company, institute or unit"
              onChange={(event) => update("organisation")(event.target.value)}
            />
          </label>

          <label>
            <span>Interested in</span>
            <select
              value={form.interest}
              onChange={(event) => update("interest")(event.target.value)}
            >
              {INTERESTS.map((interest) => (
                <option key={interest}>{interest}</option>
              ))}
            </select>
          </label>

          <label>
            <span>Your requirement</span>
            <textarea
              rows={4}
              value={form.message}
              placeholder="Quantity, timeline, deployment environment…"
              onChange={(event) => update("message")(event.target.value)}
            />
          </label>

          <div className="form-actions">
            <a className="primary-action" href={mailHref}>
              <Mail size={15} strokeWidth={2} />
              Email Us
            </a>
            <a className="whatsapp-action" href={whatsAppHref} target="_blank" rel="noreferrer">
              <Send size={15} strokeWidth={2} />
              WhatsApp Us
            </a>
          </div>
        </form>
      </div>
    </section>
  );
};
